// src/pages/inventory/suppliers/EditSupplierDrawer.tsx
import React, { useState, useEffect } from "react";
import { extractValidationErrors } from "../../../../utils/apiError";
import debounce from "lodash/debounce";
import { Button, Form, Input, Modal, Row, Col } from "antd";
import { SaveOutlined, DeleteOutlined } from "@ant-design/icons";
import { DetailDrawer } from "../../../../components/shared";
import { Supplier, createSupplier, updateSupplier, deleteSupplier } from "../../../../api/inventory/supplierApi";
import { useNotifications } from "../../../../context/NotificationContext";

const { TextArea } = Input;

interface EditSupplierDrawerProps {
	isOpen: boolean;
	onClose: () => void;
	supplier: Supplier;
}

const EditSupplierDrawer: React.FC<EditSupplierDrawerProps> = ({ isOpen, onClose, supplier }) => {
	const [updatedSupplier, setUpdatedSupplier] = useState<Supplier>({ ...supplier });
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [errorMessages, setErrorMessages] = useState<string[]>([]);
	const { showError, showSuccess } = useNotifications();

	useEffect(() => {
		setUpdatedSupplier({ ...supplier });
	}, [supplier]);

	const saveSupplier = async () => {
		setIsSubmitting(true);
		setErrorMessages([]);
		try {
			if (!updatedSupplier.name) {
				setErrorMessages(["Supplier name is required."]);
				setIsSubmitting(false);
				return;
			}

			if (supplier.supplier_id) {
				const response = await updateSupplier(supplier.supplier_id, updatedSupplier);
				setUpdatedSupplier(response);
				showSuccess("Supplier updated successfully");
			} else {
				const newSupplier = await createSupplier(updatedSupplier);
				setUpdatedSupplier(newSupplier);
				showSuccess("Supplier created successfully");
			}
		} catch (error: unknown) {
			handleErrors(error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleAutoSave = debounce(() => (supplier.supplier_id ? saveSupplier() : undefined), 500);

	const handleDelete = () => {
		Modal.confirm({
			title: "Delete Supplier",
			content: "Are you sure you want to delete this supplier? This action cannot be undone.",
			okText: "Delete",
			okType: "danger",
			cancelText: "Cancel",
			onOk: async () => {
				try {
					if (supplier.supplier_id) {
						await deleteSupplier(supplier.supplier_id);
						showSuccess("Supplier deleted successfully");
						onClose();
					}
				} catch (error) {
					handleErrors(error);
				}
			},
		});
	};

	const handleErrors = (error: unknown) => {
		setErrorMessages(extractValidationErrors(error));
		showError("Failed to save supplier");
	};

	const handleChange = (field: string, value: any) => {
		setUpdatedSupplier((prev) => ({ ...prev, [field]: value }));
	};

	return (
		<DetailDrawer
			open={isOpen}
			onClose={onClose}
			title={supplier.supplier_id ? `Edit ${supplier.name}` : "Create New Supplier"}
			subtitle={updatedSupplier.email || "No email provided"}
			headerActions={
				<div className="flex gap-2">
					<Button type="primary" icon={<SaveOutlined />} onClick={saveSupplier} loading={isSubmitting}>
						Save
					</Button>
					{supplier.supplier_id && (
						<Button danger icon={<DeleteOutlined />} onClick={handleDelete} disabled={isSubmitting}>
							Delete
						</Button>
					)}
				</div>
			}
			errorMessages={errorMessages}
			onClearErrors={() => setErrorMessages([])}
			width="600px"
		>
			<Form layout="vertical">
				<Row gutter={[16, 16]}>
					<Col xs={24}>
						<Form.Item label="Supplier Name" required>
							<Input value={updatedSupplier.name || ""} onChange={(e) => handleChange("name", e.target.value)} onBlur={handleAutoSave} placeholder="Enter supplier name" />
						</Form.Item>
					</Col>
					<Col xs={24}>
						<Form.Item label="Contact Name">
							<Input value={updatedSupplier.contact_name || ""} onChange={(e) => handleChange("contact_name", e.target.value)} onBlur={handleAutoSave} placeholder="Enter contact person name" />
						</Form.Item>
					</Col>
					<Col xs={24}>
						<Form.Item label="Email">
							<Input type="email" value={updatedSupplier.email || ""} onChange={(e) => handleChange("email", e.target.value)} onBlur={handleAutoSave} placeholder="Enter email address" />
						</Form.Item>
					</Col>
					<Col xs={24}>
						<Form.Item label="Phone">
							<Input value={updatedSupplier.phone || ""} onChange={(e) => handleChange("phone", e.target.value)} onBlur={handleAutoSave} placeholder="Enter phone number" />
						</Form.Item>
					</Col>
					<Col xs={24}>
						<Form.Item label="Address">
							<TextArea rows={3} value={updatedSupplier.address || ""} onChange={(e) => handleChange("address", e.target.value)} onBlur={handleAutoSave} placeholder="Enter supplier address" />
						</Form.Item>
					</Col>
				</Row>
			</Form>
		</DetailDrawer>
	);
};

export default EditSupplierDrawer;
